import {
	CheckCircleFilled,
	CloseCircleFilled,
	GoogleOutlined,
	ReloadOutlined,
	WarningFilled,
} from '@ant-design/icons';
import { Alert, Button, Modal, Spin, Tag, message } from 'antd';
import { useMemo, useState } from 'react';
import moment from 'moment';

import { useAuth } from '../../../../store/AuthContext/AuthContext';
import { authRequest } from '../../../../common/request/authRequest';
import { DrawerPanelTitle, modalPanelStyles } from '../../common/Drawers/DrawerPanelTitle';

export const VERIFY_STATUS: Record<string, string> = {
	title: 'Nombre',
	storefrontAddress: 'Dirección',
	categories: 'Categorías',
	serviceItems: 'Servicios',
	phoneNumbers: 'Teléfonos',
	websiteUri: 'Web',
	regularHours: 'Horarios',
	specialHours: 'Festivos',
	metadata: 'Atributos',
	openInfo: 'Estado de apertura',
	latlng: 'Coordenadas',
};

/** Persisted by Google sync but excluded from UI alerts. */
export const HIDDEN_GOOGLE_UPDATE_FIELDS = new Set(['relationshipData']);

export const visibleGoogleFields = (fields: string[] | null | undefined): string[] =>
	(fields ?? []).filter((field) => !HIDDEN_GOOGLE_UPDATE_FIELDS.has(field));

export const GAIN_VOICE_ACTION_MESSAGES: Record<string, { label: string; detail: string }> = {
	VERIFICATION: {
		label: 'Verificación requerida',
		detail: 'Es necesario completar el proceso de verificación del perfil de empresa en Google.',
	},
	WAIT_FOR_VOICE: {
		label: 'En espera de validación',
		detail: 'Google está procesando la validación del perfil. El cambio puede demorarse unos días.',
	},
	COMPLY_WITH_GUIDELINES: {
		label: 'Suspendida o Inhabilitada',
		detail: 'La información de la ficha no cumple con las directrices de Google. Revisa las políticas del Perfil de Empresa.',
	},
	RESOLVE_OWNERSHIP_CONFLICTS: {
		label: 'Duplicada, conflicto de propiedad',
		detail: 'Existe un conflicto con la propiedad del perfil. Es necesario solicitar o reclamar el acceso.',
	},
	UNKNOWN: {
		label: 'Causa desconocida',
		detail: 'Google no ha especificado una causa concreta para el estado no operativo.',
	},
};

export const getNonOperableLabel = (gainVoiceOfMerchantAction?: string | null) => {
	if (!gainVoiceOfMerchantAction || gainVoiceOfMerchantAction === 'UNKNOWN') {
		return 'No operable';
	}
	return GAIN_VOICE_ACTION_MESSAGES[gainVoiceOfMerchantAction]?.label || 'No operable';
};

export type GoogleFieldSeverity = 'critical' | 'warning' | 'low';

const CRITICAL_FIELDS = new Set(['title', 'categories', 'storefrontAddress', 'latlng', 'openInfo']);
const LOW_SEVERITY_FIELDS = new Set(['metadata']);

export const GOOGLE_FIELD_SEVERITY_COLOR: Record<GoogleFieldSeverity, string> = {
	critical: '#dc2626',
	warning: '#FF7518',
	low: '#64748b',
};

export const GOOGLE_FIELD_SEVERITY_SOFT: Record<GoogleFieldSeverity, {
	background: string;
	border: string;
}> = {
	critical: {
		background: '#fef2f2',
		border: '#fca5a5',
	},
	warning: {
		background: '#fff4eb',
		border: '#ffd0a8',
	},
	low: {
		background: '#f3f4f6',
		border: '#d1d5db',
	},
};

export const getGoogleFieldSeverity = (fieldKey: string): GoogleFieldSeverity => {
	if (CRITICAL_FIELDS.has(fieldKey)) return 'critical';
	if (LOW_SEVERITY_FIELDS.has(fieldKey)) return 'low';
	return 'warning';
};

export const getGoogleFieldTagStyle = (fieldKey: string) => {
	const severity = getGoogleFieldSeverity(fieldKey);
	const color = GOOGLE_FIELD_SEVERITY_COLOR[severity];
	const soft = GOOGLE_FIELD_SEVERITY_SOFT[severity];
	return {
		margin: 0,
		borderRadius: 6,
		fontSize: 11,
		lineHeight: '18px',
		background: soft.background,
		border: `1px solid ${soft.border}`,
		color,
	};
};

const FIELD_SEVERITY_RANK: Record<GoogleFieldSeverity, number> = {
	critical: 3,
	warning: 2,
	low: 1,
};

export const getFieldsMaxSeverity = (fields: string[]): GoogleFieldSeverity | null => {
	if (!fields.length) return null;
	return fields.reduce<GoogleFieldSeverity>((max, field) => {
		const severity = getGoogleFieldSeverity(field);
		return FIELD_SEVERITY_RANK[severity] > FIELD_SEVERITY_RANK[max] ? severity : max;
	}, 'low');
};

export const GOOGLE_SEVERITY_CARD_STYLE: Record<GoogleFieldSeverity, {
	background: string;
	border: string;
	text: string;
}> = {
	critical: {
		background: 'whitesmoke',
		border: '#fca5a5',
		text: '#dc2626',
	},
	warning: {
		background: 'whitesmoke',
		border: '#ffd0a8',
		text: '#FF7518',
	},
	low: {
		background: 'whitesmoke',
		border: '#d1d5db',
		text: '#64748b',
	},
};

export const matchesGoogleFieldFilter = (record: any, fieldKey: string) => {
	if (!fieldKey) return true;
	if (HIDDEN_GOOGLE_UPDATE_FIELDS.has(fieldKey)) return false;
	const googleStatus = record.googleStatus || record.location?.googleStatus;
	const updatedFields = visibleGoogleFields(googleStatus?.updatedFields);
	const pendingFields = visibleGoogleFields(googleStatus?.pendingFields);
	return updatedFields.includes(fieldKey) || pendingFields.includes(fieldKey);
};

export const getGoogleAlertFlags = (record: any) => {
	const meta = record.location?.metadata;
	const googleStatus = record.googleStatus || record.location?.googleStatus;
	const isNotOperative =
		googleStatus?.hasVoiceOfMerchant === false ||
		(googleStatus?.hasVoiceOfMerchant == null && meta?.hasVoiceOfMerchant === false);
	const pendingFields = visibleGoogleFields(googleStatus?.pendingFields);
	const updatedFields = visibleGoogleFields(googleStatus?.updatedFields);
	const rawPendingCount = googleStatus?.pendingFields?.length ?? 0;
	const rawUpdatedCount = googleStatus?.updatedFields?.length ?? 0;
	// Persist relationshipData but ignore it for UI; keep generic meta flags only if no field list
	const hasPendingEdits =
		pendingFields.length > 0 || (!!meta?.hasPendingEdits && rawPendingCount === 0);
	const hasGoogleUpdated =
		updatedFields.length > 0 || (!!meta?.hasGoogleUpdated && rawUpdatedCount === 0);
	const isOk = !isNotOperative && !hasPendingEdits && !hasGoogleUpdated;

	return { isNotOperative, hasPendingEdits, hasGoogleUpdated, isOk };
};

export const getGoogleAlertSeverity = (record: any): 'critical' | 'warning' | 'low' | 'ok' => {
	const googleStatus = record.googleStatus || record.location?.googleStatus;
	const { isNotOperative, hasPendingEdits, hasGoogleUpdated } = getGoogleAlertFlags(record);
	const hasWarnings = isNotOperative || hasPendingEdits || hasGoogleUpdated;
	const allAlertFields = [
		...visibleGoogleFields(googleStatus?.pendingFields),
		...visibleGoogleFields(googleStatus?.updatedFields),
	];
	const hasCriticalField = allAlertFields.some((field) => getGoogleFieldSeverity(field) === 'critical');
	const hasWarningField = allAlertFields.some((field) => getGoogleFieldSeverity(field) === 'warning');

	if (isNotOperative || hasCriticalField) return 'critical';
	if (hasWarningField || (hasWarnings && allAlertFields.length === 0)) return 'warning';
	if (hasWarnings) return 'low';
	return 'ok';
};

export type SyncGoogleStatus = {
	syncAlertTime?: string;
	updatedFields?: string[];
	pendingFields?: string[];
	hasVoiceOfMerchant?: boolean;
	hasBusinessAuthority?: boolean;
	gainVoiceOfMerchantAction?: string;
};

const getGoogleStatus = (syncLocation: any): SyncGoogleStatus =>
	syncLocation?.googleStatus || syncLocation?.location?.googleStatus || {};

export const GoogleUpdateModal = ({
	location: syncLocation,
	onCancel,
	onRefreshed,
}: {
	location: any;
	onCancel: () => void;
	onRefreshed?: (status: SyncGoogleStatus) => void;
}) => {
	const { projectGroup } = useAuth();
	const gbpLocation = syncLocation?.location || syncLocation;

	const [status, setStatus] = useState<SyncGoogleStatus>(() => getGoogleStatus(syncLocation));
	const [isRefreshing, setIsRefreshing] = useState(false);

	const updatedFields = visibleGoogleFields(status.updatedFields);
	const pendingFields = visibleGoogleFields(status.pendingFields);
	const hasVoice = status.hasVoiceOfMerchant ?? gbpLocation?.metadata?.hasVoiceOfMerchant;
	const voiceAction = status.gainVoiceOfMerchantAction;
	const actionKey = voiceAction || 'UNKNOWN';
	const actionInfo = GAIN_VOICE_ACTION_MESSAGES[actionKey] || GAIN_VOICE_ACTION_MESSAGES.UNKNOWN;

	const isOperative = hasVoice === true;
	const isNotOperative = hasVoice === false;

	const updatedSeverity = getFieldsMaxSeverity(updatedFields);
	const pendingSeverity = getFieldsMaxSeverity(pendingFields);
	const updatedCardStyle = updatedSeverity ? GOOGLE_SEVERITY_CARD_STYLE[updatedSeverity] : null;
	const pendingCardStyle = pendingSeverity ? GOOGLE_SEVERITY_CARD_STYLE[pendingSeverity] : null;

	const syncAlertLabel = useMemo(() => {
		if (!status.syncAlertTime) return null;

		const syncMoment = moment(status.syncAlertTime);
		if (!syncMoment.isValid()) return null;

		return `Actualizado ${syncMoment.fromNow()}`;
	}, [status.syncAlertTime]);

	const refreshAlerts = async () => {
		const locationName = gbpLocation?.name;
		if (!locationName) {
			message.error('No se pudo identificar la ficha');
			return;
		}

		setIsRefreshing(true);
		try {
			const data: SyncGoogleStatus = await authRequest(
				`${process.env.REACT_APP_API}/projects/${projectGroup.project}/googleUpdates/${locationName}/fields`,
				{ method: 'GET' }
			);

			const nextStatus: SyncGoogleStatus = {
				syncAlertTime: data.syncAlertTime || new Date().toISOString(),
				updatedFields: data.updatedFields || [],
				pendingFields: data.pendingFields || [],
				hasVoiceOfMerchant: data.hasVoiceOfMerchant,
				hasBusinessAuthority: data.hasBusinessAuthority,
				gainVoiceOfMerchantAction: data.gainVoiceOfMerchantAction,
			};

			setStatus(nextStatus);
			onRefreshed?.(nextStatus);
			message.success('Alertas de Google actualizadas');
		} catch {
			message.error('Error al refrescar las alertas de Google');
		} finally {
			setIsRefreshing(false);
		}
	};

	return (
		<Modal
			open={true}
			footer={null}
			onCancel={onCancel}
			width={'60%'}
			{...modalPanelStyles}
			title={
				<DrawerPanelTitle
					title="Estado de la ficha de Google Business Profile"
					location={gbpLocation || syncLocation}
				/>
			}
		>
			{/* BANNER PRINCIPAL DE ESTADO */}
			<div
				style={{
					padding: '16px 20px',
					marginBottom: '24px',
					display: 'flex',
					alignItems: 'flex-start',
					gap: '16px',
					borderLeft: `6px solid ${isOperative ? '#52c41a' : '#ff4d4f'}`,
					backgroundColor: isOperative ? '#f6ffed' : '#fff2f0',
					boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
				}}
			>
				<div style={{ fontSize: '28px', marginTop: '2px' }}>
					{isOperative ? (
						<CheckCircleFilled style={{ color: '#52c41a' }} />
					) : (
						<CloseCircleFilled style={{ color: '#ff4d4f' }} />
					)}
				</div>

				<div style={{ flex: 1 }}>
					<div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
						<Tag
							color={isOperative ? '#276749' : '#9b2c2c'}
							style={{
								color: '#ffffff',
								fontSize: '13px',
								padding: '2px 10px',
								fontWeight: 600,
								borderRadius: '4px',
								border: 'none',
							}}
						>
							{isOperative ? 'FICHA OPERATIVA' : 'FICHA NO OPERATIVA'}
						</Tag>
					</div>

					<p style={{ margin: 0, fontSize: '14px', color: '#595959' }}>
						{isOperative
							? 'Tu ficha de empresa está activa y visible correctamente en Google Maps y Buscador.'
							: voiceAction
								? actionInfo.detail
								: 'Esta ficha no está operativa en Google. Puedes refrescar las alertas para consultar el detalle.'}
					</p>

					{isNotOperative && voiceAction && (
						<div style={{ marginTop: '8px', fontSize: '13px', fontWeight: 600, color: '#cf1322' }}>
							Motivo: {actionInfo.label}
						</div>
					)}
				</div>
			</div>

			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					gap: 12,
					marginBottom: 12,
				}}
			>
				<h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#22325d' }}>
					Alertas de Google
				</h3>
				<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
					{syncAlertLabel && (
						<span style={{ fontSize: 13, color: '#5a6b7d' }}>{syncAlertLabel}</span>
					)}
					<Button
						type="link"
						icon={<ReloadOutlined />}
						loading={isRefreshing}
						onClick={refreshAlerts}
						style={{ padding: 0, height: 'auto', fontWeight: 500 }}
					>
						Refrescar
					</Button>
				</div>
			</div>

			<Spin spinning={isRefreshing} tip="Actualizando información de Google...">
				<div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
					{updatedFields.length === 0 ? (
						<Alert
							message={
								<span>
									Sin alertas de <b>cambios de Google</b>
								</span>
							}
							description="Ninguna acción requerida respecto a actualizaciones automáticas."
							type="success"
							showIcon
						/>
					) : (
						<Alert
							message={
								<div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
									<span style={{ color: updatedCardStyle?.text }}>
										Alerta de <b>cambios de Google</b>:
									</span>
									{updatedFields.map((item: string) => (
										<Tag key={item} style={getGoogleFieldTagStyle(item)}>
											{VERIFY_STATUS[item] || item}
										</Tag>
									))}
								</div>
							}
							description={
								<span style={{ color: updatedCardStyle?.text }}>
									Visita la ficha de Google para revisar estos cambios.
								</span>
							}
							showIcon
							icon={<WarningFilled style={{ color: updatedCardStyle?.text }} />}
							style={{
								background: updatedCardStyle?.background,
								border: `1px solid ${updatedCardStyle?.border}`,
							}}
						/>
					)}

					{pendingFields.length === 0 ? (
						<Alert
							message={
								<span>
									Sin alertas de <b>cambios pendientes de validación</b>
								</span>
							}
							description="Ninguna acción requerida."
							type="success"
							showIcon
						/>
					) : (
						<Alert
							message={
								<div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
									<span style={{ color: pendingCardStyle?.text }}>
										Alerta de <b>cambios pendientes de validación</b>:
									</span>
									{pendingFields.map((item: string) => (
										<Tag key={item} style={getGoogleFieldTagStyle(item)}>
											{VERIFY_STATUS[item] || item}
										</Tag>
									))}
								</div>
							}
							description={
								<span style={{ color: pendingCardStyle?.text }}>
									Esto puede provocar diferencias temporales con la información pública.
								</span>
							}
							showIcon
							icon={<WarningFilled style={{ color: pendingCardStyle?.text }} />}
							style={{
								background: pendingCardStyle?.background,
								border: `1px solid ${pendingCardStyle?.border}`,
							}}
						/>
					)}
				</div>
			</Spin>

			<div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
				{gbpLocation?.metadata?.mapsUri && (
					<Button
						style={{
							border: '1px solid #1890ff',
							background: '#fff',
							color: '#1890ff',
							borderRadius: '15px',
							display: 'flex',
							alignItems: 'center',
							gap: '6px',
							fontSize: '14px',
						}}
						type="default"
					>
						<a
							style={{
								color: '#1890ff',
								display: 'flex',
								alignItems: 'center',
								gap: '6px',
								textDecoration: 'none',
								fontSize: '14px',
							}}
							target="_blank"
							rel="noreferrer"
							href={gbpLocation.metadata?.mapsUri}
						>
							<GoogleOutlined /> Revisar en Google
						</a>
					</Button>
				)}
			</div>
		</Modal>
	);
};
